
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Menu, ToggleLeft, Settings, Users, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUserType } from "@/hooks/useUserType";

type NavigationItem = {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  childRoutes?: string[];
  condition?: () => boolean;
};

export const NavigationItems = ({ onHero = false }: { onHero?: boolean }) => {
  const location = useLocation();
  const { userType } = useUserType();
  
  // Define navigation items with conditions for specialized roles
  const navigationItems: NavigationItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { 
      path: '/switches', 
      label: 'Switches', 
      icon: ToggleLeft,
      childRoutes: ['/new-switch', '/test-switch', '/switches/']
    },
    { path: '/contacts', label: 'Contacts', icon: Users },
    { 
      path: '/admin', 
      label: 'Admin', 
      icon: ShieldCheck,
      condition: () => userType === 'admin',
      childRoutes: ['/admin/']
    },
  ];

  // Filter items based on conditions
  const filteredItems = navigationItems.filter(item => 
    !item.condition || item.condition()
  );

  // Determine if a route is active based on current path
  const isRouteActive = (item: NavigationItem) => {
    // Exact match for the main route
    if (location.pathname === item.path) {
      return true;
    }
    
    // Check if current path matches any of the defined child routes
    if (item.childRoutes) {
      return item.childRoutes.some(childRoute => {
        // For paths ending with '/', check if the current path starts with this route
        // (handles /switches/:id and /admin/ patterns)
        if (childRoute.endsWith('/')) {
          return location.pathname.startsWith(childRoute);
        }
        // For specific paths like /new-switch, do an exact match
        return location.pathname === childRoute;
      });
    }
    
    return false;
  };

  // Mobile menu
  const MobileNav = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          aria-label="Open menu"
          className={onHero ? "md:hidden text-white hover:bg-white/10" : "md:hidden text-black hover:bg-black/10"}
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {filteredItems.map((item) => (
          <DropdownMenuItem key={item.path} asChild>
            <Link to={item.path} className="flex items-center gap-2">
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Desktop navigation
  const DesktopNav = () => (
    <nav className="hidden md:flex items-center gap-4">
      {filteredItems.map((item) => (
        <TooltipProvider key={item.path}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                asChild
                className={`${
                  isRouteActive(item) ? (onHero ? 'bg-white/10' : 'bg-black/10') : ''
                } ${onHero ? 'text-white hover:bg-white/10' : 'text-black hover:bg-black/10'}`}
              >
                <Link to={item.path} aria-label={item.label}>
                  <item.icon className="h-5 w-5" aria-hidden="true" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{item.label}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </nav>
  );

  return (
    <>
      <MobileNav />
      <DesktopNav />
    </>
  );
};
